import { useAuth } from "./auth-store";

/**
 * Single point of HTTP integration with the Worker API.
 *
 * Adds the `Authorization: Bearer <token>` header on every request.
 * On 401 it clears the auth store (the LoginGate will redirect).
 *
 * Resolution order for the API base URL:
 *   1. `VITE_API_BASE_URL` from build env (lets ops override per deploy).
 *   2. In dev (import.meta.env.DEV): empty string, falls through to Vite's
 *      proxy on /api/* to localhost:8787.
 *   3. In prod: the public Cloudflare Worker URL. Cloudflare Pages doesn't
 *      serve the API itself; calls must go cross-origin to the Worker.
 *      The Worker's CORS allow-list (CORS_ALLOWED_ORIGINS env in
 *      wrangler.jsonc) must include the Pages origin.
 */

const PROD_WORKER_URL =
  "https://ai-leasing-os-worker.rodriguegaulandeaumaster.workers.dev";

const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL ??
  (import.meta.env.PROD ? PROD_WORKER_URL : "")
).replace(/\/+$/, "");

export function apiUrl(path: string): string {
  if (!API_BASE_URL) return path;
  return `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export interface ApiSuccess<T> {
  ok: true;
  data: T;
}

export interface ApiError {
  ok: false;
  error: string;
  message?: string;
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

export class HttpError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`HTTP ${status}`);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = useAuth.getState().token;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(apiUrl(path), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    /* non-JSON response */
  }

  if (res.status === 401) {
    // Clear the bad token so the LoginGate kicks in.
    useAuth.getState().clearToken();
    throw new HttpError(401, payload);
  }
  if (!res.ok) {
    throw new HttpError(res.status, payload);
  }

  // The Worker returns `{ ok: true, data: ... }`; unwrap for callers.
  if (
    payload &&
    typeof payload === "object" &&
    "ok" in payload &&
    (payload as { ok: boolean }).ok === true &&
    "data" in payload
  ) {
    return (payload as { data: T }).data;
  }

  // A 200 with an empty / non-JSON body is a backend bug. Throw a real
  // error instead of returning `null as T` and letting downstream code
  // crash on `.length` / `.map`. With this, react-query's `data` stays
  // undefined and default-destructuring (`= []`) actually works.
  if (payload == null) {
    throw new HttpError(
      res.status,
      { ok: false, error: "empty_response", path },
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

/**
 * Quick connectivity + token validation probe.
 * Returns true if the Worker is up AND the token is accepted (or unset).
 * The LoginPage uses this to validate the token before persisting it.
 */
export async function probeHealthAndAuth(token: string): Promise<{
  workerOk: boolean;
  dbOk: boolean;
  tokenAccepted: boolean | "unknown";
}> {
  // Health is public - proves the worker is reachable + DB ok.
  let workerOk = false;
  let dbOk = false;
  try {
    const res = await fetch(apiUrl("/api/health"));
    if (res.ok) {
      const j = (await res.json()) as { ok?: boolean; db_ok?: boolean };
      workerOk = Boolean(j.ok);
      dbOk = Boolean(j.db_ok);
    }
  } catch {
    /* worker unreachable */
  }

  // Token validation: hit a protected route and check the status.
  let tokenAccepted: boolean | "unknown" = "unknown";
  try {
    const res = await fetch(apiUrl("/api/conversations?limit=1"), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401) tokenAccepted = false;
    else if (res.ok) tokenAccepted = true;
  } catch {
    tokenAccepted = "unknown";
  }

  return { workerOk, dbOk, tokenAccepted };
}
